import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";

import Utils from "../../../../utils/utils";
import { useHasPermission } from "../../../../common/Hooks";
import { useSharedSection } from "../SharedSectionContext.js";
import Forms from "../../../../services/forms";
import SearchBar from "../../../controls/SearchBar";
import EditSection from "../templates/modals/EditSection.jsx";
import ListStatus from "../controls/ListStatus";
import Icon from "../../../controls/Icon.jsx";
import ConfirmationPrompt from "../../../controls/ConfirmationPrompt.jsx";

const SharedSectionsList = () => {
    const { objectType, id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const ref = useRef();
    const history = useHistory();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });
    const { sharedSectionNames, refreshSharedSectionNames } = useSharedSection();

    const [showCreateSection, setShowCreateSection] = useState(false);
    const [deleteSharedSection, setDeleteSharedSection] = useState(null);
    const [sharedSections, setSharedSections] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloadError, setHasDownloadError] = useState(false);
    const [searchPrompt, setSearchPrompt] = useState('');

    const getSharedSections = useCallback(async (query) => {
        setIsLoading(true);

        const response = await Forms.getSharedSections({ deploymentId, query });
        if (!response.error && response.data) {
            setSharedSections(response.data);
        }
        else {
            setHasDownloadError(true);
        }

        setIsLoading(false);
    }, [deploymentId]);

    useEffect(() => {
        getSharedSections();
    }, [getSharedSections]);

    const selectSharedSection = useCallback((sharedSection) => {
        history.push(`/${objectType}/${deploymentId}/shared-sections/${sharedSection._id}`);
    }, [history, objectType, deploymentId]);

    const handleDeleteSection = useCallback(async () => {
        await Forms.deleteSharedSection({
            deploymentId,
            sharedSectionGroupingId: deleteSharedSection.sharedSectionGroupingId
        });

        await Promise.all([
            getSharedSections(),
            refreshSharedSectionNames()
        ]);
        setDeleteSharedSection(null);
    }, [deleteSharedSection, deploymentId, getSharedSections, refreshSharedSectionNames]);

    const deleteSectionModal = useMemo(() => (
        <ConfirmationPrompt
            show={!!deleteSharedSection}
            message={translate("OTM_FORMS_SHARED_SECTIONS_CONFIRM_DELETE")}
            onConfirm={handleDeleteSection}
            onCancel={() => {
                setDeleteSharedSection(null);
            }}
        />
    ), [deleteSharedSection, handleDeleteSection, translate]);

    const sectionsList = useMemo(() => {
        if (sharedSections.length > 0) {
            return (
                <div className="flex-lg-grow-1 overflow-y-lg-auto padded-scrollbar">
                    <div className="list-group">
                        {sharedSections.map((section) => (
                            <div key={section._id} className="d-flex">
                                <div
                                    className="list-group-item list-group-item-action small p-3 clickable"
                                    onClick={() => {
                                        selectSharedSection(section);
                                    }}

                                >
                                    <span className="d-block h6 clickable">{section.name}</span>
                                    {section.description ? <span className="d-block mt-2 clickable">{section.description}</span> : null}
                                </div>
                                {permissions.delete ? <Button
                                    size="sm"
                                    variant="outline-danger"
                                    className={`ms-2 p-0 border-0 ${section.canDelete ? '' : 'visibility-hidden'}`}
                                    onClick={() => {
                                        setDeleteSharedSection(section);
                                    }}
                                    title={translate("OTM_DELETE")}
                                >
                                    <Icon icon="Delete" className="d-block" />
                                </Button> : null}
                            </div>
                        ))}
                    </div>
                </div>
            )
        }

        return <ListStatus loading={isLoading} error={hasDownloadError} empty={true} ></ListStatus>;
    }, [sharedSections, selectSharedSection, isLoading, hasDownloadError, permissions.delete, translate]);

    const addSectionModal = useMemo(() => (
        <EditSection
            show={showCreateSection}
            allowUpdate={permissions.update}
            isShared={true}
            isEdit={false}
            defaultShowSave={false}
            sectionNames={sharedSectionNames}
            onAdd={(section) => {
                setShowCreateSection(false);
                selectSharedSection(section);
            }}
            onClose={() => {
                setShowCreateSection(false);
            }}
        />
    ), [showCreateSection, selectSharedSection, permissions.update, sharedSectionNames]);

    return <>
        {addSectionModal}
        {deleteSectionModal}
        <div className="d-flex align-items-center mb-3">
            <SearchBar
                ref={ref}
                initialValue={searchPrompt}
                size="sm"
                placeholder={translate("OTM_SEARCH")}
                onChange={(value) => {
                    setSearchPrompt(value);
                    getSharedSections({
                        name: value,
                        description: value
                    });
                }}
            />
            {permissions.update ? (
                <Button
                    size="sm"
                    className="ms-3"
                    onClick={() => {
                        setShowCreateSection(true);
                    }}
                    disabled={isLoading}
                >
                    {translate("OTM_ADD")}
                </Button>
            ) : permissions.delete ? (
                <Button
                    size="sm"
                    className="ms-3 visibility-hidden" // align with delete buttons if present
                    disabled={true}
                >
                    <Icon icon="Delete" className="d-block" />
                </Button>
            ) : null}
        </div>
        {sectionsList}
    </>
};

export default SharedSectionsList;